// Game state manager

import type { TownState } from './townState';
import { computeTownState } from './townState';
import type { Achievement } from '../data/achievements';
import { checkNewAchievements, getAllUnlockedAchievements } from '../data/achievements';
import { QUESTS, getNextQuest, isChapterComplete, getCompletedQuestCount, applyQuestOverrides } from '../data/quests';
import type { Quest } from '../data/quests';
import { ExperienceConfig } from '../shared/types';
import * as api from '../api';

export interface GameState {
    sessionId: string | null;
    publicToken: string | null;
    answers: Record<string, string>;
    currentChapter: 1 | 2 | 3;
    townState: TownState;
    unlockedAchievements: string[];
    valentineMeter: number;
    coins: number;
    isViewOnly: boolean;
    experienceConfig?: ExperienceConfig;
}

export class GameStateManager {
    private state: GameState;
    private listeners: Set<(state: GameState) => void> = new Set();

    constructor() {
        this.state = {
            sessionId: null,
            publicToken: null,
            answers: {},
            currentChapter: 1,
            townState: computeTownState({}),
            unlockedAchievements: [],
            valentineMeter: 0,
            coins: 0,
            isViewOnly: false,
        };
    }

    getState(): GameState {
        return this.state;
    }

    setExperienceConfig(config: ExperienceConfig): void {
        this.state = {
            ...this.state,
            experienceConfig: config
        };

        if (config.customQuestions && config.customQuestions.length > 0) {
            applyQuestOverrides(config.customQuestions);

            // Re-read: I defined applyQuestOverrides in '../data/quests'.
            // I should import it.
        }
        this.notify();
    }

    subscribe(listener: (state: GameState) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify(): void {
        for (const listener of this.listeners) {
            listener(this.state);
        }
    }

    // Initialize session
    async initSession(): Promise<void> {
        const storedSessionId = api.getStoredSessionId();

        if (storedSessionId) {
            try {
                const sessionData = await api.rehydrateSession(storedSessionId);
                this.state = {
                    ...this.state,
                    sessionId: sessionData.sessionId,
                    publicToken: sessionData.publicToken,
                    answers: sessionData.answers,
                    townState: computeTownState(sessionData.answers),
                    unlockedAchievements: getAllUnlockedAchievements(sessionData.answers).map((a) => a.id),
                    valentineMeter: this.calculateValentineMeter(sessionData.answers),
                    coins: this.calculateCoins(sessionData.answers),
                    currentChapter: this.calculateCurrentChapter(sessionData.answers),
                    isViewOnly: false,
                };
                this.notify();
                return;
            } catch (e) {
                console.warn('Failed to rehydrate session, creating new one', e);
            }
        }

        // Create new session
        const session = await api.createSession();
        this.state = {
            ...this.state,
            sessionId: session.sessionId,
            publicToken: session.publicToken,
            isViewOnly: false,
        };
        this.notify();
    }

    // Initialize from share token (view-only)
    async initFromShareToken(shareToken: string): Promise<void> {
        const summary = await api.getPublicSummary(shareToken);
        this.state = {
            ...this.state,
            sessionId: null,
            publicToken: shareToken,
            answers: summary.answers,
            townState: computeTownState(summary.answers),
            unlockedAchievements: getAllUnlockedAchievements(summary.answers).map((a) => a.id),
            valentineMeter: this.calculateValentineMeter(summary.answers),
            coins: this.calculateCoins(summary.answers),
            currentChapter: this.calculateCurrentChapter(summary.answers),
            isViewOnly: true,
        };
        this.notify();
    }

    // Answer a quest
    async answerQuest(
        questionKey: string,
        answerValue: string
    ): Promise<{ newAchievements: Achievement[] }> {
        if (!this.state.sessionId || this.state.isViewOnly) {
            throw new Error('Cannot answer in view-only mode');
        }

        // Save to API
        await api.saveAnswer(this.state.sessionId, questionKey, answerValue);

        const previousAchievements = [...this.state.unlockedAchievements];
        const newAnswers = { ...this.state.answers, [questionKey]: answerValue };
        const newAchievements = checkNewAchievements(newAnswers, previousAchievements);

        this.state = {
            ...this.state,
            answers: newAnswers,
            townState: computeTownState(newAnswers),
            unlockedAchievements: [
                ...previousAchievements,
                ...newAchievements.map((a) => a.id),
            ],
            valentineMeter: this.calculateValentineMeter(newAnswers),
            coins: this.state.coins + 10 + newAchievements.length * 25,
            currentChapter: this.calculateCurrentChapter(newAnswers),
        };

        this.notify();
        return { newAchievements };
    }

    // Get current quest
    getCurrentQuest(): Quest | null {
        return getNextQuest(this.state.answers, this.state.currentChapter);
    }

    // Get next chapter quest if current chapter is complete
    getNextChapterIfReady(): 2 | 3 | null {
        if (this.state.currentChapter === 1 && isChapterComplete(1, this.state.answers)) {
            return 2;
        }
        if (this.state.currentChapter === 2 && isChapterComplete(2, this.state.answers)) {
            return 3;
        }
        return null;
    }

    // Advance to next chapter
    advanceChapter(): void {
        const nextChapter = this.getNextChapterIfReady();
        if (nextChapter) {
            this.state = {
                ...this.state,
                currentChapter: nextChapter,
            };
            this.notify();
        }
    }

    // Check if all quests are complete
    isGameComplete(): boolean {
        return (
            isChapterComplete(1, this.state.answers) &&
            isChapterComplete(2, this.state.answers) &&
            isChapterComplete(3, this.state.answers)
        );
    }

    // Calculate valentine meter (0-100)
    private calculateValentineMeter(answers: Record<string, string>): number {
        const completed = getCompletedQuestCount(answers);
        const total = QUESTS.filter((q) => !q.condition).length;
        return Math.round((completed / total) * 100);
    }

    // Calculate coins based on answers
    private calculateCoins(answers: Record<string, string>): number {
        const questCoins = Object.keys(answers).length * 10;
        const achievementCoins = getAllUnlockedAchievements(answers).length * 25;
        return questCoins + achievementCoins;
    }

    // Calculate current chapter
    private calculateCurrentChapter(answers: Record<string, string>): 1 | 2 | 3 {
        if (!isChapterComplete(1, answers)) return 1;
        if (!isChapterComplete(2, answers)) return 2;
        return 3;
    }
}

// Singleton instance
export const gameState = new GameStateManager();
